import { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Document, Page, pdfjs } from 'react-pdf';
import { patentApi, taskApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import type { SelectedDocInfo } from '@/components/TemplatesPage';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface OADocumentViewerProps {
  selectedOfficeAction: SelectedDocInfo | null;
  selectedAppNo: string | null;
}

export function OADocumentViewer({ selectedOfficeAction, selectedAppNo }: OADocumentViewerProps) {
  const { addNotification } = useNotificationStore();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState(1.0);
  const stopPollingRef = useRef<(() => void) | null>(null);

  const loadPdf = useCallback(async () => {
    if (!selectedOfficeAction || !selectedAppNo) {
      setPdfUrl(null);
      return;
    }

    // Cleanup previous polling
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }

    try {
      setLoading(true);
      setPdfUrl(null);
      setStatus('Requesting PDF...');

      const response = await patentApi.getOfficeActionPdf(
        selectedAppNo,
        selectedOfficeAction.docId
      );

      if (response?.job_id) {
        setStatus('Generating PDF...');

        stopPollingRef.current = taskApi.startGraduatedPolling(
          async () => {
            const result = await patentApi.getPdfJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          async (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              try {
                setStatus('Downloading PDF...');
                const url = await patentApi.downloadOfficeActionPdf(
                  selectedAppNo,
                  selectedOfficeAction.docId
                );
                setPdfUrl(url);
                setLoading(false);
                setStatus('');
              } catch (error) {
                console.error('Error downloading OA PDF:', error);
                setLoading(false);
                setStatus('');
                addNotification({ type: 'error', title: 'Error', message: 'Failed to download Office Action PDF.' });
              }
            } else {
              setLoading(false);
              setStatus('');
              addNotification({ type: 'error', title: 'Error', message: `Failed to load PDF: ${result?.status || 'Unknown error'}` });
            }
          },
          (error) => {
            console.error('PDF polling error:', error);
            setLoading(false);
            setStatus('');
            addNotification({ type: 'error', title: 'Error', message: 'Failed to load Office Action PDF.' });
          },
          (_attempts, elapsedTime) => {
            setStatus(`Loading PDF... (${Math.floor(elapsedTime / 1000)}s)`);
          }
        );
      } else {
        setLoading(false);
        setStatus('');
        addNotification({ type: 'error', title: 'Error', message: 'Failed to start PDF generation.' });
      }
    } catch (error) {
      console.error('Error loading OA PDF:', error);
      setLoading(false);
      setStatus('');
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load Office Action PDF.' });
    }
  }, [selectedOfficeAction, selectedAppNo, addNotification]);

  useEffect(() => {
    loadPdf();
    return () => {
      if (stopPollingRef.current) {
        stopPollingRef.current();
        stopPollingRef.current = null;
      }
    };
  }, [loadPdf]);

  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
  };

  if (!selectedOfficeAction) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <FileText className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-sm">Select an office action to view</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        <p className="mt-2 text-sm text-slate-500">{status || 'Loading...'}</p>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <FileText className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-sm">PDF not available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 bg-white/60 flex-shrink-0">
        <span className="text-xs font-medium text-slate-600 truncate max-w-[200px]" title={selectedOfficeAction.description}>
          {selectedOfficeAction.description} ({selectedOfficeAction.date})
        </span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}>
            <ZoomOut className="w-3 h-3" />
          </Button>
          <span className="text-xs text-slate-500 w-10 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setScale((s) => Math.min(2, s + 0.1))}>
            <ZoomIn className="w-3 h-3" />
          </Button>
          {numPages > 0 && (
            <span className="text-xs text-slate-500 ml-2">{numPages} pages</span>
          )}
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col items-center gap-2 p-2">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i + 1}
                pageNumber={i + 1}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-sm"
              />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
}
